"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"
import type { NodeViewProps } from "@tiptap/react"
import { NodeViewWrapper } from "@tiptap/react"
import "./resizable-image.scss"

export const ResizableImage: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  deleteNode,
}) => {
  const { src, alt, title, width, height } = node.attrs
  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  
  const [isResizing, setIsResizing] = useState(false)
  const [initialWidth, setInitialWidth] = useState(0)
  const [initialHeight, setInitialHeight] = useState(0)
  const [initialMouseX, setInitialMouseX] = useState(0)
  const [resizeHandle, setResizeHandle] = useState<string>("")
  const [aspectRatio, setAspectRatio] = useState(1)

  // Загрузка изображения и установка соотношения сторон
  useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      const img = imageRef.current
      setAspectRatio(img.naturalWidth / img.naturalHeight)
    }
  }, [src])

  const handleImageLoad = () => {
    if (imageRef.current) {
      const img = imageRef.current
      setAspectRatio(img.naturalWidth / img.naturalHeight)
    }
  }

  const startResize = useCallback(
    (e: React.MouseEvent, handle: string) => {
      e.preventDefault()
      e.stopPropagation()
      
      if (!containerRef.current) return

      const rect = containerRef.current.getBoundingClientRect()
      setIsResizing(true)
      setInitialWidth(rect.width)
      setInitialHeight(rect.height)
      setInitialMouseX(e.clientX)
      setResizeHandle(handle)
    },
    []
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return

      const deltaX = e.clientX - initialMouseX
      let newWidth = initialWidth

      if (resizeHandle.includes("right")) {
        newWidth = Math.max(100, initialWidth + deltaX)
      } else if (resizeHandle.includes("left")) {
        newWidth = Math.max(100, initialWidth - deltaX)
      }

      // Сохраняем пропорции
      const newHeight = Math.round(newWidth / aspectRatio)

      updateAttributes({
        width: Math.round(newWidth),
        height: newHeight,
      })
    },
    [isResizing, initialWidth, initialMouseX, resizeHandle, aspectRatio, updateAttributes]
  )

  const stopResize = useCallback(() => {
    setIsResizing(false)
    setResizeHandle("")
  }, [])

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", stopResize)
      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", stopResize)
      }
    }
  }, [isResizing, handleMouseMove, stopResize])

  // Сброс размера к оригиналу
  const resetSize = () => {
    updateAttributes({
      width: null,
      height: null,
    })
  }

  return (
    <NodeViewWrapper className="resizable-image-wrapper" data-drag-handle>
      <div
        ref={containerRef}
        className={`resizable-image-container ${selected ? "selected" : ""} ${isResizing ? "resizing" : ""}`}
        style={{
          width: width ? `${width}px` : "auto",
          maxWidth: "100%",
        }}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt || ""}
          title={title || ""}
          className="resizable-image"
          onLoad={handleImageLoad}
          style={{
            width: "100%",
            height: height ? `${height}px` : "auto",
          }}
          draggable={false}
        />
        
        {/* Delete button - always visible on hover */}
        <button
          className="delete-image-btn"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            deleteNode()
          }}
          title="Удалить изображение"
        >
          ✕
        </button>
        
        {selected && (
          <>
            {/* Resize handles */}
            <div
              className="resize-handle resize-handle-left"
              onMouseDown={(e) => startResize(e, "left")}
            />
            <div
              className="resize-handle resize-handle-right"
              onMouseDown={(e) => startResize(e, "right")}
            />
            
            {/* Size info */}
            <div className="resize-info">
              {width && height ? (
                <span>{width} × {height}</span>
              ) : imageRef.current ? (
                <span>{imageRef.current.naturalWidth} × {imageRef.current.naturalHeight}</span>
              ) : null}
              {width && (
                <button 
                  className="reset-size-btn" 
                  onClick={resetSize}
                  title="Сбросить размер"
                >
                  ↺
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </NodeViewWrapper>
  )
}

export default ResizableImage
